import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';

const InvoicingSelectionContext = createContext();

export const useInvoicingSelectionContext = () => {
    const context = useContext(InvoicingSelectionContext);
    if (!context) {
        throw new Error(
            'useInvoicingSelectionContext must be used within an InvoicingSelectionContextProvider'
        );
    }
    return context;
};

export const SelectionStatus = {
    SELECTED: 'selected',
    INDETERMINATE: 'indeterminate',
    NOT_SELECTED: false,
};

const initializeSelectableItems = (type, items) =>
    items.map((item) => ({
        type,
        id: item.id,
        status: SelectionStatus.NOT_SELECTED,
    }));
const initializeSelectableItemsWithSubItems = (type, items, childrenKey) =>
    items.map((item) => ({
        type,
        id: item.id,
        hasSubItems: true,
        status: SelectionStatus.NOT_SELECTED,
        subItems:
            item[childrenKey]?.map((child) => ({
                id: child.id,
                status: SelectionStatus.NOT_SELECTED,
            })) || [],
    }));
const initializeBooking = (booking) => {
    const timesheetItems = initializeSelectableItemsWithSubItems(
        'timesheet',
        booking.timesheets || [],
        'lineItems'
    );
    const depositItems = initializeSelectableItems('deposit', booking.deposits || []);
    const expenseItems = initializeSelectableItemsWithSubItems(
        'expense',
        booking.expenses || [],
        'items'
    );
    return {
        bookingId: booking.bookingId,
        clientId: booking.clientId,
        status: SelectionStatus.NOT_SELECTED,
        items: [...timesheetItems, ...depositItems, ...expenseItems],
    };
};
const initializeState = ({ bookings, totalCount }) => ({
    selectStatus: SelectionStatus.NOT_SELECTED,
    totalCount,
    bookings: bookings?.map(initializeBooking),
});

function reducer(state, action) {
    const updateSelectStatus = (newState) => {
        const allBookingsSelected = newState.bookings.every(
            (booking) => booking.status === SelectionStatus.SELECTED
        );
        if (allBookingsSelected) {
            return { ...newState, selectStatus: SelectionStatus.SELECTED };
        }

        const someBookingsSelected = newState.bookings.some(
            (booking) =>
                booking.status === SelectionStatus.SELECTED ||
                booking.status === SelectionStatus.INDETERMINATE
        );
        return {
            ...newState,
            selectStatus: someBookingsSelected
                ? SelectionStatus.INDETERMINATE
                : SelectionStatus.NOT_SELECTED,
        };
    };

    const updateBookingStatus = (newState, bookingIndex) => {
        const bookingCopy = { ...newState.bookings[bookingIndex] };
        const allItemsSelected = bookingCopy.items.every(
            (item) => item.status === SelectionStatus.SELECTED
        );
        if (allItemsSelected) {
            bookingCopy.status = SelectionStatus.SELECTED;
        } else {
            const someItemsSelected = bookingCopy.items.some(
                (item) =>
                    item.status === SelectionStatus.SELECTED ||
                    item.status === SelectionStatus.INDETERMINATE
            );
            bookingCopy.status = someItemsSelected
                ? SelectionStatus.INDETERMINATE
                : SelectionStatus.NOT_SELECTED;
        }
        const newStateCopy = { ...newState };
        newStateCopy.bookings[bookingIndex] = bookingCopy;
        return updateSelectStatus(newStateCopy);
    };

    switch (action.type) {
        case 'initialize_new_bookings': {
            const newSelectStatus = [
                SelectionStatus.SELECTED,
                SelectionStatus.INDETERMINATE,
            ].includes(state.selectStatus)
                ? SelectionStatus.INDETERMINATE
                : SelectionStatus.NOT_SELECTED;
            return {
                ...state,
                selectStatus: newSelectStatus,
                bookings: [...state.bookings, ...action.payload.bookings.map(initializeBooking)],
            };
        }

        case 'toggle_all_bookings': {
            const newSelectStatus =
                state.selectStatus === SelectionStatus.SELECTED
                    ? SelectionStatus.NOT_SELECTED
                    : SelectionStatus.SELECTED;
            return {
                ...state,
                selectStatus: newSelectStatus,
                bookings: state.bookings.map((booking) => ({
                    ...booking,
                    status: newSelectStatus,
                    items: booking.items.map((item) =>
                        item.hasSubItems
                            ? {
                                  ...item,
                                  status: newSelectStatus,
                                  subItems: item.subItems.map((subItem) => ({
                                      ...subItem,
                                      status: newSelectStatus,
                                  })),
                              }
                            : { ...item, status: newSelectStatus }
                    ),
                })),
            };
        }

        case 'select_booking': {
            const { bookingId } = action.payload;
            const bookingIndex = state.bookings.findIndex((item) => item.bookingId === bookingId);
            if (bookingIndex === -1) return state;

            const bookingCopy = { ...state.bookings[bookingIndex] };

            if (bookingCopy.status === SelectionStatus.SELECTED) {
                // deselect all items in the booking
                bookingCopy.items = bookingCopy.items.map((item) => {
                    if (item.hasSubItems) {
                        return {
                            ...item,
                            subItems: item.subItems.map((subItem) => ({
                                ...subItem,
                                status: SelectionStatus.NOT_SELECTED,
                            })),
                            status: SelectionStatus.NOT_SELECTED,
                        };
                    }
                    return { ...item, status: SelectionStatus.NOT_SELECTED };
                });
                bookingCopy.status = SelectionStatus.NOT_SELECTED;
            } else {
                // select all items in the booking
                bookingCopy.items = bookingCopy.items.map((item) => {
                    if (item.hasSubItems) {
                        return {
                            ...item,
                            subItems: item.subItems.map((subItem) => ({
                                ...subItem,
                                status: SelectionStatus.SELECTED,
                            })),
                            status: SelectionStatus.SELECTED,
                        };
                    }
                    return { ...item, status: SelectionStatus.SELECTED };
                });
                bookingCopy.status = SelectionStatus.SELECTED;
            }

            const newState = { ...state };
            newState.bookings[bookingIndex] = bookingCopy;
            return updateSelectStatus(newState);
        }

        case 'select_item': {
            const { bookingId, itemType, itemId } = action.payload;
            const bookingIndex = state.bookings.findIndex((item) => item.bookingId === bookingId);
            if (bookingIndex === -1) return state;
            const itemIndex = state.bookings[bookingIndex].items.findIndex(
                (item) => item.type === itemType && item.id === itemId
            );
            if (itemIndex === -1) return state;
            const itemCopy = { ...state.bookings[bookingIndex].items[itemIndex] };
            if (itemCopy.hasSubItems) {
                // toggle the status of the item && sub items
                if (itemCopy.status === SelectionStatus.SELECTED) {
                    // unselect the item and all sub items
                    itemCopy.status = SelectionStatus.NOT_SELECTED;
                    itemCopy.subItems = itemCopy.subItems.map((subItem) => ({
                        ...subItem,
                        status: SelectionStatus.NOT_SELECTED,
                    }));
                } else {
                    // select the item and all sub items
                    itemCopy.status = SelectionStatus.SELECTED;
                    itemCopy.subItems = itemCopy.subItems.map((subItem) => ({
                        ...subItem,
                        status: SelectionStatus.SELECTED,
                    }));
                }
            } else {
                // toggle the status of the item
                itemCopy.status =
                    itemCopy.status === SelectionStatus.SELECTED
                        ? SelectionStatus.NOT_SELECTED
                        : SelectionStatus.SELECTED;
            }
            const newState = { ...state };
            newState.bookings[bookingIndex].items[itemIndex] = itemCopy;
            return updateBookingStatus(newState, bookingIndex);
        }

        case 'update_sub_items_selection': {
            const { bookingId, itemType, itemId, subItemIds: newSubItemIds } = action.payload;
            const bookingIndex = state.bookings.findIndex((item) => item.bookingId === bookingId);
            if (bookingIndex === -1) return state;
            const itemIndex = state.bookings[bookingIndex].items.findIndex(
                (item) => item.type === itemType && item.id === itemId
            );
            if (itemIndex === -1) return state;
            const itemCopy = { ...state.bookings[bookingIndex].items[itemIndex] };
            itemCopy.subItems = itemCopy.subItems.map((subItem) => ({
                ...subItem,
                status: newSubItemIds.includes(subItem.id)
                    ? SelectionStatus.SELECTED
                    : SelectionStatus.NOT_SELECTED,
            }));
            if (newSubItemIds.length === itemCopy.subItems.length) {
                itemCopy.status = SelectionStatus.SELECTED;
            } else if (newSubItemIds.length === 0) {
                itemCopy.status = SelectionStatus.NOT_SELECTED;
            } else {
                itemCopy.status = SelectionStatus.INDETERMINATE;
            }
            const newState = { ...state };
            newState.bookings[bookingIndex].items[itemIndex] = itemCopy;
            return updateBookingStatus(newState, bookingIndex);
        }

        case 'deselect_all_items': {
            return {
                ...state,
                selectStatus: SelectionStatus.NOT_SELECTED,
                bookings: state.bookings.map((booking) => ({
                    ...booking,
                    status: SelectionStatus.NOT_SELECTED,
                    items: booking.items.map((item) =>
                        item.hasSubItems
                            ? {
                                  ...item,
                                  status: SelectionStatus.NOT_SELECTED,
                                  subItems: item.subItems.map((subItem) => ({
                                      ...subItem,
                                      status: SelectionStatus.NOT_SELECTED,
                                  })),
                              }
                            : { ...item, status: SelectionStatus.NOT_SELECTED }
                    ),
                })),
            };
        }

        default: {
            throw Error(`Unknown action: ${action.type}`);
        }
    }
}

/*  state is an object with the following structure:
    {
        selectStatus: SelectionStatus,
        bookings: [{ 
            bookingId: number, 
            status: SelectionStatus,
            items: [
                { type: string, id: number, status: SelectionStatus, subItems: [{ id: number, selected: boolean }] }, // has sub items
                { type: string, id: number, selected: boolean }, // no sub items
            ]
        }]
    }
*/
export const InvoicingSelectionContextProvider = ({ children, bookings, totalCount }) => {
    const [state, dispatch] = useReducer(reducer, { bookings, totalCount }, initializeState);

    const handleInitializeNewBookings = useCallback((newBookings) => {
        dispatch({ type: 'initialize_new_bookings', payload: { bookings: newBookings } });
    }, []);

    const handleSelectAllBookings = useCallback(() => {
        dispatch({ type: 'toggle_all_bookings' });
    }, []);

    const handleSelectBooking = useCallback((bookingId) => {
        dispatch({ type: 'select_booking', payload: { bookingId } });
    }, []);

    const handleSelectItem = useCallback((bookingId, itemType, itemId) => {
        dispatch({ type: 'select_item', payload: { bookingId, itemType, itemId } });
    }, []);

    const handleUpdateSubItemsSelection = useCallback((bookingId, itemType, itemId, subItemIds) => {
        dispatch({
            type: 'update_sub_items_selection',
            payload: { bookingId, itemType, itemId, subItemIds },
        });
    }, []);

    const deselectAllItems = useCallback(() => {
        dispatch({ type: 'deselect_all_items' });
    });

    // count the number of top level items that are selected (bookings)
    const selectedBookingsCount = useMemo(
        () =>
            state.bookings.filter((booking) => booking.status === SelectionStatus.SELECTED).length,
        [state]
    );

    // count the total number of selected items (top level items and sub items)
    const selectedItemsCount = useMemo(
        () =>
            state?.bookings?.reduce((acc, booking) => {
                if (!booking?.items || booking?.items?.length === 0) {
                    return booking.status === SelectionStatus.SELECTED ? acc + 1 : acc;
                }
                let newSelectedItems = 0;
                booking.items.forEach((item) => {
                    if (item.hasSubItems) {
                        item.subItems.forEach((subItem) => {
                            if (subItem.status === SelectionStatus.SELECTED) {
                                newSelectedItems++;
                            }
                        });
                    } else if (item.status === SelectionStatus.SELECTED) {
                        newSelectedItems++;
                    }
                });
                return acc + newSelectedItems;
            }, 0),
        [state]
    );

    const selectedItems = useMemo(() => {
        // Helper function to map item types to API format
        // ProcessedTimeEntry|ProcessedMileageEntry|Expense|CreditMemo|Deposit|FinanceFee
        function getItemType(type) {
            switch (type) {
                case 'timesheet':
                    return 'ProcessedTimeEntry';
                case 'expense':
                    return 'Expense';
                case 'deposit':
                    return 'Deposit';
                case 'mileage':
                    return 'ProcessedMileageEntry';
                case 'creditMemo':
                    return 'CreditMemo';
                case 'financeFee':
                    return 'FinanceFee';
                default:
                    return '';
            }
        }

        if (!state?.bookings?.length) return [];

        return state.bookings.reduce((acc, booking) => {
            // Skip bookings without items or with no selected items
            if (!booking?.items || booking.items.length === 0) {
                return acc;
            }

            const includedItems = [];

            booking.items.forEach((item) => {
                if (item.hasSubItems) {
                    item.subItems.forEach((subItem) => {
                        if (subItem.status === SelectionStatus.SELECTED) {
                            includedItems.push({
                                itemType: getItemType(item.type),
                                itemId: subItem.id,
                            });
                        }
                    });
                } else if (item.status === SelectionStatus.SELECTED) {
                    includedItems.push({
                        itemType: getItemType(item.type),
                        itemId: item.id,
                    });
                }
            });

            if (includedItems.length > 0) {
                acc.push({
                    bookingId: booking?.bookingId,
                    clientId: booking?.clientId,
                    included: includedItems,
                });
            }

            return acc;
        }, []);
    }, [state]);

    const getBookingSelectionState = useCallback(
        (bookingId) => {
            if (state.selectStatus === SelectionStatus.SELECTED) return SelectionStatus.SELECTED;

            return (
                state?.bookings?.find((item) => item.bookingId === bookingId)?.status ||
                SelectionStatus.NOT_SELECTED
            );
        },
        [state]
    );

    const getBookingItemSelectionState = useCallback(
        (bookingId, itemType, itemId) => {
            if (state.selectStatus === SelectionStatus.SELECTED) return SelectionStatus.SELECTED;

            const booking = state?.bookings?.find((item) => item.bookingId === bookingId);
            if (!booking || booking.status === SelectionStatus.NOT_SELECTED)
                return SelectionStatus.NOT_SELECTED;
            if (booking.status === SelectionStatus.SELECTED) return SelectionStatus.SELECTED;
            return (
                booking.items.find((item) => item.type === itemType && item.id === itemId)
                    ?.status || SelectionStatus.NOT_SELECTED
            );
        },
        [state]
    );

    const getSelectedSubItems = useCallback(
        (bookingId, itemType, itemId) => {
            const booking = state?.bookings?.find((item) => item.bookingId === bookingId);
            if (!booking || booking.status === SelectionStatus.NOT_SELECTED) return [];
            const bookingItem = booking.items?.find(
                (item) => item.type === itemType && item.id === itemId
            );
            if (!bookingItem || !bookingItem.hasSubItems) return [];
            return (
                bookingItem.subItems
                    ?.filter((subItem) => subItem.status === SelectionStatus.SELECTED)
                    ?.map(({ id }) => id) || []
            );
        },
        [state]
    );

    useEffect(() => {
        const existingBookingsIds = state.bookings?.map((booking) => booking.bookingId);
        const missingBookings = bookings?.filter(
            (booking) => !existingBookingsIds.includes(booking.bookingId)
        );
        if (missingBookings && missingBookings?.length > 0) {
            handleInitializeNewBookings(missingBookings);
        }
    }, [bookings]);

    const memoizedContextValue = useMemo(
        () => ({
            selectStatus: state.selectStatus,
            selectedBookingsCount,
            selectedItemsCount,
            selectedItems,
            handleSelectAllBookings,
            handleSelectBooking,
            handleSelectItem,
            handleUpdateSubItemsSelection,
            getBookingSelectionState,
            getBookingItemSelectionState,
            getSelectedSubItems,
            deselectAllItems,
        }),
        [
            state.selectStatus,
            selectedBookingsCount,
            selectedItemsCount,
            selectedItems,
            handleSelectAllBookings,
            handleSelectBooking,
            handleSelectItem,
            handleUpdateSubItemsSelection,
            getBookingSelectionState,
            getBookingItemSelectionState,
            getSelectedSubItems,
            deselectAllItems,
        ]
    );

    return (
        <InvoicingSelectionContext.Provider value={memoizedContextValue}>
            {typeof children === 'function' ? children(memoizedContextValue) : children}
        </InvoicingSelectionContext.Provider>
    );
};
